<template>
  <Card class="p-6" data-testid="api-key-card">
    <div class="flex items-start justify-between">
      <div class="flex-1 min-w-0">
        <!-- Header with name and status -->
        <div class="flex items-start justify-between mb-3">
          <div class="flex-1 min-w-0">
            <h3 class="text-lg font-medium text-gray-900 dark:text-white truncate">
              {{ apiKey.name }}
            </h3>
            <div class="mt-1 flex items-center space-x-2">
              <code
                class="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
              >
                {{ apiKey.key_prefix }}****
              </code>
              <span
                v-if="expiryInfo.isExpiring"
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
              >
                Expiring soon
              </span>
            </div>
          </div>
          <span
            :class="[
              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
              statusInfo.color,
            ]"
          >
            {{ statusInfo.text }}
          </span>
        </div>

        <!-- Key Type & Permissions -->
        <div class="mb-4">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {{ apiKey.key_type === 'scope' ? 'Scope' : 'Permissions' }}
          </h4>
          <div class="flex flex-wrap gap-1">
            <template v-if="apiKey.key_type === 'scope' && apiKey.scope">
              <span class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                {{ scopeLabel }}
              </span>
            </template>
            <template v-else-if="apiKey.permissions && apiKey.permissions.length > 0">
              <span
                v-for="perm in displayPermissions"
                :key="perm"
                class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium font-mono bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
              >
                {{ perm }}
              </span>
              <span
                v-if="apiKey.permissions.length > 4"
                class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
              >
                +{{ apiKey.permissions.length - 4 }} more
              </span>
            </template>
          </div>
        </div>

        <!-- Metadata -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-gray-500 dark:text-gray-400">Created</span>
            <p class="text-gray-900 dark:text-white font-medium">
              {{ formatDate(apiKey.created_at) }}
            </p>
          </div>
          <div>
            <span class="text-gray-500 dark:text-gray-400">Expires</span>
            <p class="text-gray-900 dark:text-white font-medium">
              {{ apiKey.expires_at ? formatDate(apiKey.expires_at) : 'Never' }}
            </p>
          </div>
        </div>

        <!-- Expiry Warning -->
        <div
          v-if="expiryInfo.isExpiring || expiryInfo.isExpired"
          class="mt-4 p-3 rounded-md"
          :class="
            expiryInfo.isExpired
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
              : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700'
          "
        >
          <div class="flex items-center">
            <svg
              class="h-4 w-4 mr-2"
              :class="expiryInfo.isExpired ? 'text-red-400' : 'text-yellow-400'"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <span
              class="text-sm font-medium"
              :class="
                expiryInfo.isExpired
                  ? 'text-red-800 dark:text-red-200'
                  : 'text-yellow-800 dark:text-yellow-200'
              "
            >
              {{ expiryInfo.message }}
            </span>
          </div>
        </div>

        <!-- Usage Stats (if available) -->
        <div
          v-if="showUsage && usage"
          class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600"
        >
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Usage Statistics
          </h4>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div class="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <p class="font-semibold text-gray-900 dark:text-white">
                {{ usage.total_requests.toLocaleString() }}
              </p>
              <p class="text-gray-500 dark:text-gray-400">Total</p>
            </div>
            <div class="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <p class="font-semibold text-gray-900 dark:text-white">
                {{ usage.successful_requests.toLocaleString() }}
              </p>
              <p class="text-gray-500 dark:text-gray-400">Success</p>
            </div>
            <div class="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <p class="font-semibold text-gray-900 dark:text-white">
                {{ usage.error_requests.toLocaleString() }}
              </p>
              <p class="text-gray-500 dark:text-gray-400">Errors</p>
            </div>
            <div class="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <p class="font-semibold text-gray-900 dark:text-white">
                {{ usage.unique_ips }}
              </p>
              <p class="text-gray-500 dark:text-gray-400">IPs</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Menu -->
      <div class="flex-shrink-0 ml-4">
        <div class="relative">
          <Button
            variant="ghost"
            size="sm"
            @click.stop="toggleMenu"
            class="p-2"
            data-testid="api-key-actions"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 5v.01M12 12v.01M12 19v.01"
              />
            </svg>
          </Button>

          <!-- Dropdown Menu -->
          <div
            v-if="showMenu"
            v-click-outside="() => (showMenu = false)"
            class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black dark:ring-gray-600 ring-opacity-5 focus:outline-none z-50"
          >
            <div class="py-1">
              <button
                @click.stop="handleViewUsage"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                View Usage
              </button>

              <button
                v-if="apiKey.key_type === 'fine_grained'"
                @click.stop="handleEditPermissions"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                data-testid="edit-permissions-button"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Edit Permissions
              </button>

              <button
                @click.stop="handleRegenerate"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                data-testid="regenerate-key-button"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Rotate Key
              </button>

              <hr class="my-1" />

              <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dropdown menu action item, not a standalone Button -->
              <button
                @click.stop="handleDelete"
                class="w-full text-left px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                data-testid="delete-key-button"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Revoke
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Card, Button } from '@asabdev/asab-ui';
  import {
    getApiKeyStatus,
    getApiKeyStatusColor,
    formatApiKeyDate,
    isApiKeyExpired,
    isApiKeyExpiringSoon,
  } from '@/features/api-keys/utils';
  import { SCOPE_LABELS } from '@/features/api-keys/types';
  import type { ApiKey, ApiKeyUsageStats } from '@/features/api-keys/types';

  interface Props {
    apiKey: ApiKey;
    usage?: ApiKeyUsageStats;
    showUsage?: boolean;
  }

  interface Emits {
    (e: 'delete', apiKey: ApiKey): void;
    (e: 'view-usage', apiKey: ApiKey): void;
    (e: 'regenerate', apiKey: ApiKey): void;
    (e: 'edit-permissions', apiKey: ApiKey): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    showUsage: false,
  });

  const emit = defineEmits<Emits>();

  // Local state
  const showMenu = ref(false);

  // Computed properties
  const scopeLabel = computed(() => {
    if (props.apiKey.scope) {
      return SCOPE_LABELS[props.apiKey.scope] || props.apiKey.scope;
    }
    return '';
  });

  const displayPermissions = computed(() => {
    if (!props.apiKey.permissions) return [];
    return props.apiKey.permissions.slice(0, 4);
  });

  const statusInfo = computed(() => {
    const status = getApiKeyStatus(props.apiKey);
    const label = status === 'expiring_soon' ? 'Expiring Soon' : status.charAt(0).toUpperCase() + status.slice(1);
    return {
      status,
      color: getApiKeyStatusColor(status),
      text: label,
    };
  });

  const expiryInfo = computed(() => {
    const isExpired = isApiKeyExpired(props.apiKey);
    const isExpiring = isApiKeyExpiringSoon(props.apiKey);

    let message = '';
    if (isExpired) {
      message = 'This API key has expired';
    } else if (isExpiring && props.apiKey.expires_at) {
      const daysLeft = Math.ceil(
        (new Date(props.apiKey.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      message = `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`;
    }

    return {
      isExpired,
      isExpiring,
      message,
    };
  });

  // Methods
  const formatDate = (dateString: string) => formatApiKeyDate(dateString);

  const toggleMenu = () => {
    showMenu.value = !showMenu.value;
  };

  const handleDelete = () => {
    showMenu.value = false;
    emit('delete', props.apiKey);
  };

  const handleViewUsage = () => {
    showMenu.value = false;
    emit('view-usage', props.apiKey);
  };

  const handleEditPermissions = () => {
    showMenu.value = false;
    emit('edit-permissions', props.apiKey);
  };

  const handleRegenerate = () => {
    showMenu.value = false;
    emit('regenerate', props.apiKey);
  };

  // Click outside directive
  const vClickOutside = {
    beforeMount(
      el: HTMLElement & { clickOutsideEvent?: (event: Event) => void },
      binding: { value: () => void }
    ) {
      el.clickOutsideEvent = (event: Event) => {
        if (!(el === event.target || el.contains(event.target as Node))) {
          binding.value();
        }
      };
      document.addEventListener('click', el.clickOutsideEvent);
    },
    unmounted(el: HTMLElement & { clickOutsideEvent?: (event: Event) => void }) {
      if (el.clickOutsideEvent) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    },
  };
</script>
